import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAddArtistAccount, useUpdateArtistAccountMeta } from "@/api/mutations";
import { useGetMyAccounts } from "@/api/queries";
// import useDomainConfigAuth from "@/api/sanity/queries/getSiteConfigWithDomain";
import { apiCall } from "@/api/shared";
import AuthPrimaryButton from "@/components/AuthPrimaryButton";
import FormError from "@/components/FormError";
import P from "@/components/P";
import Spacer from "@/components/Spacer";
import { sendEvent } from "@/helpers/helpers";
// import AppState from "@/store/appstate";
import useAppStore, { persistAccountToLocalStorage } from "@/store/appStateZustand";

function AddAccountFinal() {
  const { t } = useTranslation("onboarding");
  const navigate = useNavigate();

  // const accountData = AppState.useState(s => s.addAccount);
  // const spotifyLinkMethod = AppState.useState(s => s.spotifyAccountMethod);

  const accountData = useAppStore(state => state.addAccount || {});
  // More detailed debug logging to trace account data
  console.log("accountData", accountData);
  console.log("Account name value:", accountData.name);
  console.log("Account object keys:", Object.keys(accountData));

  const spotifyLinkMethod = useAppStore(state => state.spotifyAccountMethod);

  const [mutationErrorMessage, setMutationErrorMessage] = useState("");
  const [validationErrorMessage, setValidationErrorMessage] = useState("");
  const [pending, setPending] = useState(false);
  const myAccounts = useGetMyAccounts();
  // const { data: domainConfig } = useDomainConfigAuth();

  const addMutation = useAddArtistAccount();
  const accountMetaMutation = useUpdateArtistAccountMeta();

  const updateAccountMeta = async (aac) => {
    // update account meta
    const data = {
      account_id: aac,
      facebook_obj: accountData?.facebook,
      spotify_obj: accountData?.spotify,
      songkick_obj: accountData?.songkick,
      twitter_obj: accountData?.twitter,
      bandsintown_obj: accountData?.bandsintown,
      instagram_obj: accountData?.instagram,
    };

    accountMetaMutation.reset();

    accountMetaMutation.mutate(data, {
      onSuccess: (response) => {
        if (response.failedvalidation) {
          setValidationErrorMessage(response.data.messages);
          setPending(false);
        }
        else {
          setTimeout(() => {
            myAccounts.refetch();
            setTimeout(async () => {
              const kyc = await apiCall({
                endpoint: "/rightsapp/get",
                path: "/api/v1/user/getkyc",
              });

              // Set the newly created account as the active account
              // First, get the refreshed accounts data and find the account we just created
              const refreshedAccounts = await myAccounts.refetch();
              const newAccount = refreshedAccounts.data?.artistaccounts?.find(
                account => account.artistaccountcode === aac,
              );

              if (newAccount) {
                console.log("[AddAccountFinal] Setting newly created account as active:", newAccount.artistaccountcode);
                useAppStore.setState({
                  selectedAccount: newAccount.artistaccountcode,
                  selectedCatalogue: newAccount.cataloguecode,
                });

                // Persist to localStorage for cross-session recovery
                persistAccountToLocalStorage(newAccount.artistaccountcode);
              }
              else {
                console.warn("[AddAccountFinal] Could not find newly created account in refreshed data");
              }

              setPending(false);

              // Old state update (commented out for reference)
              /*
              AppState.update((s) => {
                s.addAccount = {
                  name: null,
                  live: null,
                  streaming: null,
                  spotify: null,
                  twitter: null,
                  facebook: null,
                  songkick: null,
                  bandsintown: null,
                  instagram: null,
                };
                s.kyc = kyc;
              });
              */

              // New Zustand state update
              useAppStore.setState({
                addAccount: {
                  name: null,
                  live: null,
                  streaming: null,
                  spotify: null,
                  twitter: null,
                  facebook: null,
                  songkick: null,
                  bandsintown: null,
                  instagram: null,
                },
                kyc,
              });

              if (kyc?.messages && kyc.messages.length > 0 && kyc.messages[0] === "KYC Required") {
                navigate({ to: "/security" });
              }
              else {
                navigate({ to: "/" });
              }
            }, 2000);
          }, 1000);
        }
      },
      onError: (error) => {
        setMutationErrorMessage(error.message);
        setPending(false);
      },
    });
  };

  const addAccount = async () => {
    setPending(true);
    setMutationErrorMessage("");
    setValidationErrorMessage("");

    console.log("Adding account with name:", accountData.name);
    console.log("Full account data:", accountData);

    // Check if account name is provided
    if (!accountData.name) {
      setValidationErrorMessage("Account Name is required");
      setPending(false);
      return;
    }

    // add artist account to rights app
    const data = {
      cataloguecode: accountData.cataloguecode || "",
      accountname: accountData.name,
      facebookpageurl: accountData.facebook,
      spotifyid: accountData.spotify
        ? JSON.parse(accountData.spotify).id
        : null,
      releasedtoserviceorstore: accountData.streaming === true || accountData.streaming === "true",
      performedlastsixmonths: accountData.live === true || accountData.live === "true",
      twitterprofileurl: accountData.twitter
        ? `https://twitter.com/${accountData.twitter}`
        : null,
      soundcloudpageurl: null,
    };

    addMutation.reset();

    addMutation.mutate(data, {
      onSuccess: (result, response) => {
        if (response.failedvalidation) {
          setValidationErrorMessage(response.data.messages);
          setPending(false);
        }
        else {
          const aac = result?.model?.artistaccountcode;
          updateAccountMeta(aac);
        }
        if (spotifyLinkMethod === "searchWithName") {
          sendEvent({ property: "pe14489857_spotify_account_name" });
        }
        else if (spotifyLinkMethod === "searchWithURL") {
          sendEvent({ property: "pe14489857_spotify_account_link" });
        }
      },
      onError: (error) => {
        setMutationErrorMessage(error.message);
        setPending(false);
      },
    });
  };

  return (
    <div className="container mx-auto">
      <div className="flex flex-col items-center justify-center p-4 text-center text-white min-h-screen-minus-footer">
        <Spacer />
        <P>{t("onboarding:completed-account-setup")}</P>

        <Spacer />
        <div className="w-1/2">
          <AuthPrimaryButton
            disabled={pending}
            pending={pending}
            onClick={() => {
              if (pending)
                return; // Prevent multiple clicks
              setPending(true);
              addAccount();
            }}
            arrow
          >
            {t("onboarding:go-to-dashboard")}
          </AuthPrimaryButton>
        </div>
        <FormError mutationError={mutationErrorMessage} validationError={validationErrorMessage} />
      </div>
    </div>
  );
}

export default AddAccountFinal;
